import React from "react";
import { Grid, SpaceBetween } from "@amzn/awsui-components-console";
import {
  FixedFeeConfig,
  FixedFeeType,
} from "@amzn/fuse-accounting-central-datastore-client";
import Collapsible from "src/pages/commons/collapsible";
import { Domain } from "src/config/BartLambdaConfig";
import { TopLevelTable } from "./top-level-table";
import { EstimateBreakdownTable } from "./estimate-breakdown-table";
import { InvoiceBreakdownTable } from "./invoice-breakdown-table";
import { AdjustmentsTable } from "./adjustments-table";
import "src/lambda/transactionSearchLambda/payload-display.css";
import { AdditionalInfoContainer } from "./additional-info-container";
import { TacBreakdownTable } from "./tac-breakdown-table";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
  domain: Domain;
}

// Main display component
export const FixedFeeConfigDisplay: React.FC<Props> = ({
  fixedFeeConfig,
  domain,
}) => {
  // Full JSON dump in collapsible section
  const fullJsonBlob = (
    <Collapsible open={false} title="Full JSON Output">
      <pre className="sourceInfo">
        {JSON.stringify(fixedFeeConfig, null, 2)}
      </pre>
    </Collapsible>
  );

  let breakdownRow;

  const hasValidBreakdown = (breakdown: Record<string, any> | undefined) =>
    breakdown && Object.keys(breakdown).length > 0;

  if (fixedFeeConfig.fixedFeeType === FixedFeeType.TERMINATION_AT_CONVENIENCE) {
    // TaC: TaC Breakdown + Invoice Breakdown (50%-50% in width)
    if (
      hasValidBreakdown(fixedFeeConfig.tacBreakdown) &&
      hasValidBreakdown(fixedFeeConfig.invoiceBreakdown)
    ) {
      breakdownRow = (
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <TacBreakdownTable fixedFeeConfig={fixedFeeConfig} />
          <InvoiceBreakdownTable fixedFeeConfig={fixedFeeConfig} />
        </Grid>
      );
    } else {
      breakdownRow = (
        <>
          <TacBreakdownTable fixedFeeConfig={fixedFeeConfig} />
          <InvoiceBreakdownTable fixedFeeConfig={fixedFeeConfig} />
        </>
      );
    }
  } else {
    // Standard: Estimate Breakdown + Invoice Breakdown (25%-75% in width)
    if (
      hasValidBreakdown(fixedFeeConfig.estimateBreakdown) &&
      hasValidBreakdown(fixedFeeConfig.invoiceBreakdown)
    ) {
      breakdownRow = (
        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
          <EstimateBreakdownTable fixedFeeConfig={fixedFeeConfig} />
          <InvoiceBreakdownTable fixedFeeConfig={fixedFeeConfig} />
        </Grid>
      );
    } else {
      breakdownRow = (
        <>
          <EstimateBreakdownTable fixedFeeConfig={fixedFeeConfig} />
          <InvoiceBreakdownTable fixedFeeConfig={fixedFeeConfig} />
        </>
      );
    }
  }

  return (
    <div style={{ paddingLeft: "10px" }}>
      <SpaceBetween size="l">
        <TopLevelTable fixedFeeConfig={fixedFeeConfig} />
        {breakdownRow}
        <AdjustmentsTable fixedFeeConfig={fixedFeeConfig} domain={domain} />
        <AdditionalInfoContainer
          fixedFeeConfig={fixedFeeConfig}
          domain={domain}
        />
        {fullJsonBlob}
      </SpaceBetween>
    </div>
  );
};
